import React, { Component } from 'react';
import API from 'API';
import Loader from 'components/common/Loader';
import TextHelpers from 'helpers/TextHelpers';
import moment from 'moment';
import ExpandingTextArea from 'react-expanding-textarea';
import { loadStripe } from '@stripe/stripe-js';
import {
    Elements, ElementsConsumer,
    CardNumberElement, CardExpiryElement, CardCvcElement
} from '@stripe/react-stripe-js';

//-------------------------------------------------------------------------------------------------------------------

class Summary extends Component {

    constructor(props) {
        super(props);

        this.stripe = null;
        this.stripeOptions = {
            fonts: [{
                cssSrc: 'https://fonts.googleapis.com/css2?family=Barlow:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap'
            }]
        };
        this.stripeElementOptions = {
            style: {
                base: {
                    fontFamily: 'barlow, sans-serif',
                    fontSize: '20px',
                    color: '#000',
                    padding: '10px',
                    //letterSpacing: '0.025em'
                },
                invalid: {
                    color: '#FF0000'
                }
            }
        };

        this.state = {
            isLoading: true,
            isTimeSlotUnavailable: false,
            error: null,
            isDepositPaid: false
        };
    }

    componentDidMount() {
        this.load();
        this.checkTimeSlotAvailable();
        this.startCheckingTimeSlotAvailable();
    }

    startCheckingTimeSlotAvailable() {
        this.checkTimeSlotAvailableInterval = setInterval(() => {
            this.checkTimeSlotAvailable();
        }, 30 * 1000);
    }

    componentWillUnmount() {
        clearInterval(this.checkTimeSlotAvailableInterval);
    }

    async load() {
        const { accountInfo, bookingRequest } = this.props;
        if (!bookingRequest.sessionID) {
            window.location = '/';
            return;
        }
        if (accountInfo.depositAmount > 0) {
            const initDepositPaymentResult = await API.call('book/init-deposit-payment', bookingRequest);
            if (initDepositPaymentResult.isAlreadyPaid) {
                this.setState({ isDepositPaid: true });
            } else {
                this.stripe = await loadStripe(accountInfo.stripePublicKey);
            }
        }
        this.setState({ isLoading: false });
    }

    setIsBusy(isBusy, stripeElements) {
        if (stripeElements) {
            const inputs = [
                stripeElements.getElement(CardNumberElement),
                stripeElements.getElement(CardExpiryElement),
                stripeElements.getElement(CardCvcElement)
            ];
            inputs.forEach(i => i.update({ disabled: isBusy }));
        }
        this.setState({ isBusy });
    }

    async checkTimeSlotAvailable() {
        const { isAvailable } = await API.call('book/is-time-slot-available', this.props.bookingRequest);
        if (!isAvailable) {
            this.setState({
                isTimeSlotUnavailable: true
            });
            this.props.onChange({
                timeSlot: null
            });
            clearInterval(this.checkTimeSlotAvailableInterval);
        }
        return isAvailable;
    }

    async submit(stripeElements) {
        clearInterval(this.checkTimeSlotAvailableInterval);
        this.setIsBusy(true, stripeElements);
        try {
            if (this.stripe) {
                const makeDepositPaymentResult = await this.makeDepositPayment(stripeElements);
                if (!makeDepositPaymentResult) {
                    return;
                }
            }
            await API.call('book/confirm', this.props.bookingRequest);
            this.props.onNextClicked();
            return;
        } catch (error) {
            this.setState({ error });
            this.setIsBusy(false, stripeElements);
            this.startCheckingTimeSlotAvailable();
        }
        this.startCheckingTimeSlotAvailable();
        this.setIsBusy(false, stripeElements);
    }

    async makeDepositPayment(stripeElements) {
        const { bookingRequest } = this.props;

        this.setState({ error: null });

        try {
            // Validate non-Stripe fields
            if (!bookingRequest.billingName) {
                throw 'Please provide the name on your card';
            }
            if (!bookingRequest.billingPostcode) {
                throw 'Please provide your billing postcode';
            }

            // Create payment method if needed
            if (!bookingRequest.stripePaymentMethodID) {
                const createPaymentMethodResult = await this.stripe.createPaymentMethod({
                    type: 'card',
                    card: stripeElements.getElement(CardNumberElement),
                    billing_details: {
                        name: bookingRequest.billingName,
                        address: {
                            postal_code: bookingRequest.billingPostcode
                        }
                    }
                });
                if (createPaymentMethodResult.error) {
                    this.props.onChange({ stripePaymentMethodID: null });
                    throw createPaymentMethodResult.error;
                }
                bookingRequest.stripePaymentMethodID = createPaymentMethodResult.paymentMethod.id;
                this.props.onChange({ stripePaymentMethodID: bookingRequest.stripePaymentMethodID });
            }

            // Init deposit payment
            const initDepositPaymentResult = await API.call('book/init-deposit-payment', bookingRequest);
            if (initDepositPaymentResult.error) {
                throw initDepositPaymentResult.error;
            }

            if (!initDepositPaymentResult.isAlreadyPaid) {
                // Check one last time that the desired slot is still available
                const isAvailable = await this.checkTimeSlotAvailable();
                if (!isAvailable) {
                    return false;
                }
                
                // Make the payment
                const confirmCardPaymentResult = await this.stripe.confirmCardPayment(initDepositPaymentResult.clientSecret, {
                    setup_future_usage: 'off_session',
                    payment_method: bookingRequest.stripePaymentMethodID,
                    receipt_email: bookingRequest.customerEmail
                });

                // Catch error
                if (confirmCardPaymentResult.error) {
                    throw confirmCardPaymentResult.error;
                }
            }

            return true;
        } catch (error) {
            if (error.message) {
                error = error.message;
            }
            this.setState({ error });
            this.setIsBusy(false, stripeElements);
            return false;
        }
    }

    render() {
        const { onBackClicked, onChange, accountInfo, bookingRequest } = this.props;
        const { isLoading, isTimeSlotUnavailable, isBusy } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        if (isTimeSlotUnavailable) {
            return (<>

                <h2>Sorry...</h2>
                <p className="subtitle">
                    The time slot you have chosen is no longer available.<br />
                    Please press <b>Back</b> to choose another.
                </p>

                <div className="nav-buttons">

                    <button
                        type="button"
                        className="btn btn-secondary me-auto"
                        onClick={() => onBackClicked()}
                    >
                        Back
                    </button>

                </div>

            </>);
        }

        return (<>

            <h2>Does everything look correct?</h2>
            
            <p className="subtitle">
                <b>Date</b>: {moment(bookingRequest.date).format('dddd Do MMM YYYY')}<br />
                <b>Sweep Will Arrive</b>: {bookingRequest.timeSlot}<br />
                <b>Estimated Price</b>: {TextHelpers.formatCurrency(accountInfo.sweepPrice)}<br />
                <b>Duration</b>: Around {accountInfo.standardDuration} minutes<br />
            </p>

            <h2>Anything else we need to know?</h2>
            <p className="subtitle">
                e.g. Directions for getting to your house, helpful information about your chimney, etc.
            </p>
            <ExpandingTextArea
                className="form-control mb-3 additional-notes"
                rows={2}
                defaultValue={bookingRequest.notes}
                onBlur={e => onChange({ notes: e.target.value })}
                disabled={isBusy}
            />

            {accountInfo.depositAmount > 0 ?
                this.renderTakeDeposit() :
                this.renderNavButtons()
            }

        </>);
    }
    
    renderTakeDeposit() {
        const { onChange, onBackClicked, onNextClicked, accountInfo, bookingRequest } = this.props;
        const { isBusy, isDepositPaid, error } = this.state;

        if (isDepositPaid) {
            return (<>
                <h2>Deposit Payment</h2>
                <p className="subtitle">
                    Thank you, the deposit has been paid. Please continue with the booking.
                </p>

                {this.renderNavButtons()}
            </>);
        }

        return (<>
            
            <h2>Deposit Payment</h2>

            <p className="subtitle">
                Please pay the deposit of
                {' '}
                <b>{TextHelpers.formatCurrency(accountInfo.depositAmount)}</b>
                {' '}
                to secure your booking.
            </p>

            <Elements stripe={this.stripe} options={this.stripeOptions}>
                <ElementsConsumer>
                    {({ elements }) => <>

                        <div className="stripe-form">

                            <div className="form-group mb-3">
                                <label>Name on Card</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bookingRequest.billingName || ''}
                                    onChange={e => onChange({ billingName: e.target.value })}
                                    disabled={isBusy}
                                    placeholder="Name on card"
                                />
                            </div>

                            <div className="form-group mb-3">
                                <label>Card Number</label>
                                <div className="stripe-wrapper">
                                    <CardNumberElement
                                        options={this.stripeElementOptions}
                                    />
                                </div>
                            </div>

                            <div className="form-group mb-3">
                                <label>Expiry Date</label>
                                <div className="stripe-wrapper">
                                    <CardExpiryElement
                                        options={this.stripeElementOptions}
                                    />
                                </div>
                            </div>
            
                            <div className="form-group mb-3">
                                <label>CVC</label>
                                <div className="stripe-wrapper">
                                    <CardCvcElement
                                        options={this.stripeElementOptions}
                                    />
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Postcode</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={bookingRequest.billingPostcode || ''}
                                    onChange={e => onChange({ billingPostcode: e.target.value })}
                                    disabled={isBusy}
                                    placeholder="Postcode"
                                />
                            </div>

                            {!!error &&
                                <div className="alert alert-danger mt-3 mb-3">
                                    {error}
                                </div>
                            }

                        </div>

                        {this.renderNavButtons(elements)}

                    </>}
                </ElementsConsumer>
            </Elements>
            
        </>);
    }

    renderNavButtons(stripeElements) {
        const { onBackClicked, onNextClicked, accountInfo, bookingRequest } = this.props;
        const { isBusy } = this.state;

        return (
            <div className="nav-buttons">

                <button
                    type="button"
                    className="btn btn-secondary me-auto"
                    onClick={() => onBackClicked()}
                    disabled={isBusy}
                >
                    Back
                </button>

                <button type="button" className="btn ms-auto" onClick={() => this.submit(stripeElements)} disabled={isBusy}>
                    {isBusy ?
                        <><Loader isInline />{' '}Please Wait...</> :
                        <>Confirm Booking</>
                    }
                </button>

            </div>
        );
    }

}

export default Summary;